<template>
  <div
    :class="[
      'number-text-field',
      isParticipantPortalField
        ? 'is-participant-portal-field'
        : 'is-general-field',
      { 'is-account-holder-portal-field': isAccountHolderField },
    ]"
  >
    <div
      v-if="isParticipantPortalField && showHeader"
      class="number-text-field-header-style"
    >
      <span v-if="required && !isAccountHolderField" class="error-text">*</span
      >{{ header }}
    </div>
    <v-text-field
      v-model="formattedText"
      :id="id"
      :rules="rules"
      :label="
        isParticipantPortalField
          ? ''
          : showLabel
          ? label + (required || hideOptionalText ? '' : ' (optional)')
          : ''
      "
      :validate-on-blur="validateBlur"
      :hide-details="hideDetails"
      :prefix="showPrefix"
      class="number-text-field"
      :class="{ large: large, 'text-right': textRight }"
      :disabled="disabled"
      :clearable="clearable"
      :dense="dense"
      :color="$colors.penChecksTeal"
      :hint="hint"
      :persistent-hint="persistentHint"
      :tabindex="tabindex"
      @focus="isFocused = true"
      @blur="inputBlurred"
      @keyup="keyUp"
      @change="$emit('change', floatValue)"
      ref="numbertextfield"
      :outlined="isParticipantPortalField"
      :data-id="dataId"
    ></v-text-field>
  </div>
</template>
<script>
export default {
  name: "PcNumberTextFieldReskinned",

  props: {
    value: {},
    // Forwarded to the inner Vuetify field so `document.getElementById` reaches
    // the actual input. form-error-helper and the error summary rely on it.
    id: {
      type: String,
    },
    label: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tabindex: {
      type: Number,
    },
    minValue: {
      type: Number,
    },
    maxValue: {
      type: Number,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    allowNegative: {
      type: Boolean,
      default: false,
    },
    allowZero: {
      type: Boolean,
      default: false,
    },
    wholeNumbers: {
      type: Boolean,
      default: false,
    },
    showCommas: {
      type: Boolean,
      default: false,
    },
    prefix: {
      type: String,
      default: null,
    },
    large: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
    },
    persistentHint: {
      type: Boolean,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    textRight: {
      type: Boolean,
      default: false,
    },
    hideOptionalText: {
      type: Boolean,
    },
    isParticipantPortalField: {
      type: Boolean,
      default: false,
    },
    isAccountHolderField: {
      type: Boolean,
      default: false,
    },
    header: {
      type: String,
      default: "",
    },
    showHeader: {
      type: Boolean,
      default: false,
    },
    validateBlur: {
      type: Boolean,
      default: true,
    },
    dataId: {
      type: String,
      required: true,
      validator(value) {
        return value.length == 32;
      },
    },
  },

  data: function () {
    return {
      formattedText: null,
      isFocused: false,

      rules: [
        () => {
          if (this.required && this.floatValue == null)
            return "Please enter a value.";
          return true;
        },
        () => {
          if (this.disabled) return true;

          if (!this.allowZero && this.floatValue == 0)
            return "Please enter a non-zero value.";
          return true;
        },
        () => {
          if (!this.allowNegative && this.floatValue < 0)
            return "Please enter a positive value.";
          return true;
        },
        () => {
          if (
            this.minValue != null &&
            this.floatValue != null &&
            this.floatValue < this.minValue
          )
            return "Number must be at least " + this.minValue;
          return true;
        },
        () => {
          if (
            this.maxValue != null &&
            this.floatValue != null &&
            this.floatValue > this.maxValue
          )
            return "Number may not exceed " + this.maxValue;
          return true;
        },
      ],
    };
  },

  computed: {
    showPrefix: function () {
      return this.isFocused || this.formattedText ? this.prefix : null;
    },
    floatValue: function () {
      if (this.formattedText == null || this.formattedText == "-") return null;
      return parseFloat(Number.numberFormatHelper(this.formattedText));
    },
  },

  watch: {
    value: function () {
      if (this.value != this.floatValue) {
        this.formattedText = this.value;
        this.formatText(true);
      }
    },
    floatValue: function () {
      this.$emit("input", this.floatValue);
    },
  },

  methods: {
    keyUp: function (e) {
      this.$emit("keyup", e);

      this.textChanged(e);
    },
    textChanged: function (e) {
      var selectionStart = e.target.selectionStart;
      var selectionEnd = e.target.selectionEnd;
      var value = e.target.value;

      if (value == "-") {
        this.formattedText = "-";
        return;
      }

      if (this.formattedText != null && this.formattedText.trim() == "") {
        this.formattedText = null;
        return;
      }

      var isPasting = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() == "v";

      // Dont format if not at end of string (or pasting)
      if (
        !isPasting &&
        (selectionStart != selectionEnd || selectionStart < value.length)
      ) {
        return;
      }

      if (e.key != "." && e.keyCode != 8) {
        // keycode 8 = backspace
        this.formatText();
      }
    },
    inputBlurred: function () {
      this.isFocused = false;
      this.formatText(true); // Force 2 decimals when you blur
    },
    formatText: function (force2Decimals = false) {
      if (parseFloat(this.formattedText) == 0 || this.formattedText)
        this.formattedText = Number.numberFormatHelper(this.formattedText, {
          commas: this.showCommas,
          limit2Decimals: !force2Decimals,
          force2Decimals: force2Decimals,
          stripDecimals: this.wholeNumbers,
        });

      if (this.formattedText != null && this.formattedText.toString() == "NaN")
        this.formattedText = null;
    },
  },

  mounted: function () {
    this.formattedText = this.value;
    this.formatText(true);
  },
};
</script>

<style lang="scss">
.number-text-field.is-participant-portal-field {
  .number-text-field-header-style {
    margin-left: 10px;
    margin-bottom: 8px;
    font-family: "Open Sans", sans-serif;
    color: $neutral70;
    font-size: 12px;
    font-weight: 600;
  }

  .v-input--is-focused {
    .v-input__slot {
      border-width: 1px !important;
      border-color: $primary !important;
      box-shadow: 0px 0px 0px 3px $primary10 !important;
    }
  }

  div.v-input:not(.error--text, .v-input--is-focused) {
    fieldset {
      border-color: $neutral10 !important;
    }
  }

  div.v-text-field {
    border-radius: 6px !important;
  }

  div.v-text-field__slot {
    margin-top: auto;
    margin-bottom: auto;
    height: 20px;
    font-family: "Open Sans", sans-serif;
    input {
      color: $neutral70 !important;
      font-size: 14px;
      height: 20px !important;
    }

    input::placeholder {
      font-family: "Open Sans", sans-serif !important;
      color: $neutral30 !important;
      font-weight: 400 !important;
      font-size: 14px;
    }
  }

  .v-input__slot {
    min-height: 40px !important;
    height: 20px;
  }

  .v-input--is-focused {
    .v-input__slot {
      color: $primary !important;
      caret-color: $primary !important;
      box-shadow: 0px 0px 0px 3px $primary10 !important;
      fieldset {
        border-width: 1px !important;
      }
    }
  }
}

.number-text-field.is-account-holder-portal-field {
  .v-input__slot {
    margin-bottom: 0;
  }
  .v-text-field__details {
    min-height: 0px;
    margin-bottom: 0px;
    padding-left: 0px;

    div {
      min-height: 0px;
    }

    .error--text {
      margin-top: 8px;
      margin-bottom: 8px;
    }

    .v-messages__wrapper {
      font-size: 10px;
    }
  }
}

.number-text-field.is-general-field {
  .large {
    input {
      font-size: 20px !important;
    }
  }
  .text-right {
    input {
      text-align: right;
    }
  }
}
</style>
